/**
 * Browser-autofill support for the Listbox-based Select (PD-16249).
 *
 * Browser autofill writes only into native form controls, so the Select keeps
 * a visually-hidden native input beside its Listbox button. When the browser
 * fills that input it hands over free text — the value it has on file, which
 * for a US state may be a code ("CA") or a full name ("California"), in any
 * casing — and this helper maps that text back onto an option so the Listbox
 * can select it. A value match wins over a label match; disabled options are
 * never selected; nothing matched means the control is left alone.
 */
export interface AutofillSelectOption {
	value: string | number;
	label: string;
	disabled?: boolean;
}

export function resolveAutofillOption<T extends AutofillSelectOption>(
	options: ReadonlyArray<T> | null | undefined,
	rawText: string | null | undefined,
): T | undefined {
	const needle = rawText?.trim().toUpperCase() ?? '';
	if (needle === '' || options == null) {
		return undefined;
	}

	const enabled = options.filter((option) => option.disabled !== true);

	return (
		enabled.find((option) => String(option.value).toUpperCase() === needle) ??
		enabled.find((option) => option.label.trim().toUpperCase() === needle)
	);
}
